'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { environment } from '@/config/environment';
import { useVisitorCountry } from '@/hooks/useVisitorCountry';
import type { IFeatureQuote } from '@/interfaces/feature';
import {
  chatChannelForCountry,
  isChatChannel,
  type TChatChannel,
} from '@/lib/chat-channels';
import type { ITheme } from '@/types/theme';
import FeatureStudioQuote from './FeatureStudioQuote';

const LineChatExperience = dynamic(() => import('./LineChatExperience'), {
  ssr: false,
});

// Local builds have no CloudFront country header, so always show the demo there.
const isLocalSite = new URL(environment.site.url).hostname === 'localhost';
// Countries that get the messaging demo on business-type pages (gated mode).
const GATED_COUNTRIES = new Set(['TW', 'HK', 'MO', 'CN']);

/**
 * The messaging AI demo skinned for the visitor's region: LINE by default,
 * WhatsApp in Hong Kong / Macau, WeChat in mainland China. The engine behind
 * every skin is the same. `?channel=whatsapp|wechat|line` overrides it for QA.
 *
 * `gated`: only render for the regions above (business-type pages); otherwise
 * render for everyone, with LINE until the country lookup answers (home page).
 */
export default function RegionalChatExperience({
  language,
  businessName,
  quote,
  quoteTheme,
  gated = false,
}: {
  language: 'en' | 'zh';
  businessName?: string;
  /** The business type's own quote and hero photo, shown right before the chat. */
  quote?: IFeatureQuote;
  quoteTheme?: ITheme;
  gated?: boolean;
}) {
  const { countryCode, lookedUp } = useVisitorCountry();
  const [override, setOverride] = useState<TChatChannel | null>(null);

  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get(
      'channel'
    );
    if (isChatChannel(requested)) setOverride(requested);
  }, []);

  if (gated && !override && !isLocalSite) {
    if (!lookedUp || !countryCode || !GATED_COUNTRIES.has(countryCode))
      return null;
  }
  const channel = override ?? chatChannelForCountry(countryCode);

  return (
    <>
      {quote && (
        <FeatureStudioQuote
          quote={quote}
          language={language}
          theme={quoteTheme ?? null}
        />
      )}
      <LineChatExperience
        language={language}
        businessName={businessName}
        channel={channel}
      />
    </>
  );
}
